import * as React from 'react';
import * as Yup from 'yup';
import {
  FormikHelpers,
  useFormikContext,
} from 'formik';
import { useTranslation } from 'react-i18next';
import {
  FormTextField,
  FormSelect,
  FormError,
} from '@hvcgroep/design-system';
import {
  ApiEndpointQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryDefinition,
  QueryStatus,
} from '@reduxjs/toolkit/query';
import { QueryHooks } from '@reduxjs/toolkit/dist/query/react/buildHooks';

import { QUERY_TIMEOUT_TIME } from '../../../config/constants';
import './style.scss';
import AddressSelectionInfo from '../Info';
import { AddressQueryFnParameters, AddressQueryMapper } from '../types';
import { Unpacked } from '../../../types';
import { geolocationApi } from '../../../store/slices/GeolocationApi';

export type FieldTypes = {
  postal_code: string,
  house_number: string,
  selection: string,
};

export type Props<AddressType> = {
  postalCodeRef: React.RefObject<HTMLInputElement>;
  houseNumberRef: React.RefObject<HTMLInputElement>;
  selectRef: React.RefObject<HTMLSelectElement>,
  setQueryParams: (state: AddressQueryFnParameters) => void,
  setQueryTimeout: React.Dispatch<React.SetStateAction<NodeJS.Timeout | undefined>>,
  queryTimeout: NodeJS.Timeout | undefined,
  suggestions: AddressType[],
  suggestionStatus: QueryStatus,
  submittedValues: FieldTypes | null,
  queryMapper: HookParams<AddressType[]>['queryMapper'],
  noResultsError?: string,
  isLiveValidationDisabled?: boolean
};

export const AddressSelectionFields = <
  AddressType extends {
    [key in keyof AddressType]: string | number;
  },
>(props: Props<AddressType>) => {
  const { t } = useTranslation();

  const {
    postalCodeRef,
    setQueryParams,
    setQueryTimeout,
    queryTimeout,
    houseNumberRef,
    selectRef,
    suggestions,
    suggestionStatus,
    submittedValues,
    queryMapper,
    noResultsError,
    isLiveValidationDisabled,
  } = props;

  const { values } = useFormikContext<FieldTypes>();

  const selectedAddress = getAddressSelection(suggestions, values.selection, queryMapper);

  const noAddressFound = (
    suggestions.length === 0 &&
    suggestionStatus === 'fulfilled' &&
    submittedValues === values
  );

  const serverError = suggestionStatus === 'rejected';

  return (
    <>
      <div className="AddressSelectionFields__inputs">
        <FormTextField
          className="AddressSelectionFields__input AddressSelectionFields__input--postalCode"
          ref={postalCodeRef}
          verticalSpacing={false}
          name="postal_code"
          label={t('form.field.postal_code.label')}
          placeholder={t('form.field.postal_code.placeholder')}
          isLiveValidationDisabled={isLiveValidationDisabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            clearTimeout(queryTimeout);
            if (e.target.value && values.house_number) {
              const timeout = setTimeout(() => {
                setQueryParams({
                  postal_code: e.target.value.trim().replace(/\s/g, ''),
                  housenumber: values.house_number.trim().replace(/\s/g, ''),
                });
              }, QUERY_TIMEOUT_TIME);
              setQueryTimeout(timeout);
            }
          }}
        />
        <FormTextField
          className="AddressSelectionFields__input AddressSelectionFields__input--houseNumber"
          ref={houseNumberRef}
          verticalSpacing={false}
          name="house_number"
          label={t('form.field.house_number.label')}
          placeholder={t('form.field.house_number.placeholder')}
          isLiveValidationDisabled={isLiveValidationDisabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            clearTimeout(queryTimeout);
            if (values.postal_code && e.target.value) {
              const timeout = setTimeout(() => {
                setQueryParams({
                  postal_code: values.postal_code.trim().replace(/\s/g, ''),
                  housenumber: e.target.value.trim().replace(/\s/g, ''),
                });
              }, QUERY_TIMEOUT_TIME);
              setQueryTimeout(timeout);
            }
          }}
        />
        <FormSelect
          className="AddressSelectionFields__input AddressSelectionFields__input--extension"
          ref={selectRef}
          verticalSpacing={false}
          name="selection"
          label={t('form.field.house_number_extension.label')}
          placeholder={t('form.field.house_number_extension.placeholder')}
          isLiveValidationDisabled={isLiveValidationDisabled}
          disabled={suggestions.length <= 1}
          defaultValue={selectedAddress ? selectedAddress[queryMapper.uid] : undefined}
          options={suggestions.map((suggestion) => {
            const houseLetter = queryMapper.house_letter && suggestion[queryMapper.house_letter] ? String(suggestion[queryMapper.house_letter]) : '';
            const houseNumberExtension = suggestion[queryMapper.house_number_extension] ? String(suggestion[queryMapper.house_number_extension]) : '';

            let label = '';

            if (houseLetter && houseNumberExtension) {
              label = `${houseLetter}-${houseNumberExtension}`;
            } else if (houseLetter) {
              label = houseLetter;
            } else if (houseNumberExtension) {
              label = houseNumberExtension;
            } else {
              label = t('form.field.house_number_extension.empty');
            }

            return {
              key: suggestion[queryMapper.uid],
              value: label,
            };
          })}
        />
      </div>
      <div aria-live="polite" className="d-flex mt-4">
        <p className="text-italic my-0">{t('address_selection.form.label')}: <span>{(!selectedAddress && !noAddressFound && !serverError) ? '...' : ''}</span> {noAddressFound || serverError ? <span className="text-bold text-color">{t('address_selection.form.error.label')}</span> : ''}</p>
        {selectedAddress && (
          <div className="text-italic ml-2">
            <AddressSelectionInfo
              address={selectedAddress}
              variant="simple"
              addressMapper={queryMapper}
            />
          </div>
        )}
      </div>
      {noAddressFound && (
        <FormError>
          {noResultsError || t('address_selection.form.error.no_results')}
        </FormError>
      )}
      {serverError && (
        <FormError>
          {t('address_selection.form.error.server_error')}
        </FormError>
      )}
    </>
  );
};

function getAddressSelection<AddressType>(suggestions: AddressType[], selection: string, queryMapper: AddressQueryMapper<AddressType>) {
  if (!suggestions.length) {
    return null;
  }

  const foundSuggestion = suggestions.find((suggestion) => suggestion[queryMapper.uid] === selection);

  if (foundSuggestion) {
    return foundSuggestion;
  }

  if (suggestions.length > 1) {
    return null;
  }

  return suggestions[0];
}

type RTKQueryParams<QueryResponseType> = QueryDefinition<AddressQueryFnParameters, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>, never, QueryResponseType>;

export type HookParams<QueryResponseType extends any[]> = {
  queryHooks: ApiEndpointQuery<RTKQueryParams<QueryResponseType>, any> & QueryHooks<RTKQueryParams<QueryResponseType>>;
  queryMapper: AddressQueryMapper<Unpacked<QueryResponseType>>,
  defaultAddress?: Unpacked<QueryResponseType>
};

const useAddressFields = <
  QueryResponseType extends Unpacked<QueryResponseType>[],
>(params?: Partial<HookParams<QueryResponseType>>) => {
  const defaultParams: HookParams<QueryResponseType> = {
    queryHooks: geolocationApi.endpoints.addressSearch,
    queryMapper: {
      uid: 'nummeraanduiding_id',
      street: 'straatnaam',
      house_number: 'huisnummer',
      house_number_extension: 'huisnummertoevoeging',
      house_letter: 'huisletter',
      postal_code: 'postcode',
      city: 'woonplaatsnaam',
    } as HookParams<QueryResponseType>['queryMapper'],
  } as HookParams<QueryResponseType>;

  const mergedParams = {
    ...defaultParams,
    ...params,
  };

  const {
    queryHooks,
    queryMapper,
    defaultAddress,
  } = mergedParams;

  const { t } = useTranslation();
  const postalCodeRef = React.useRef<HTMLInputElement>(null);
  const houseNumberRef = React.useRef<HTMLInputElement>(null);
  const selectRef = React.useRef<HTMLSelectElement>(null);
  const [queryParams, setQueryParams] = React.useState<AddressQueryFnParameters | null>(defaultAddress ? {
    postal_code: defaultAddress[queryMapper.postal_code] as string,
    housenumber: defaultAddress[queryMapper.house_number] as string,
  } : null);
  const [submittedValues, setSubmittedValues] = React.useState<FieldTypes | null>(null);
  const [queryTimeout, setQueryTimeout] = React.useState<NodeJS.Timeout>();

  const [trigger, {
    data: suggestions = defaultAddress ? [defaultAddress] : [],
    status: suggestionStatus,
  }] = queryHooks.useLazyQuery();

  React.useEffect(() => {
    if (!queryParams) return;
    trigger(queryParams);
  }, [queryParams]);

  return {
    initialValues: {
      postal_code: '',
      house_number: '',
      selection: '',
    },
    validationScheme: {
      postal_code: Yup.string()
        .required(t('form.error.required'))
        .matches(/^ *[1-9][0-9]{3} *(?!sa|sd|ss)[a-z]{2}$/i, t('form.error.invalid_postal_code')),
      house_number: Yup.string()
        .required(t('form.error.required')),
      selection: Yup.string().when([], {
        is: () => suggestions.length > 1,
        then: Yup.string().required(t('form.error.required')),
        otherwise: Yup.string().notRequired(),
      }),
    },
    props: {
      postalCodeRef,
      setQueryParams,
      setQueryTimeout,
      queryTimeout,
      houseNumberRef,
      selectRef,
      queryMapper,
      suggestions,
      suggestionStatus,
      submittedValues,
    },
    submit: async <
      CustomFieldTypes extends FieldTypes,
    >(values: CustomFieldTypes, helpers?: FormikHelpers<CustomFieldTypes>) => {
      setSubmittedValues(values);

      // Clear the query timeout to prevent the fetchSuggestions request below
      // from being canceled.
      clearTimeout(queryTimeout);

      // Remove focus from input elements on submit
      postalCodeRef?.current?.blur();
      houseNumberRef?.current?.blur();
      selectRef?.current?.blur();

      // Always refetch the address suggestions on submit.
      // That way if a requestTimeout hasn't finished yet we'll still get the right data, right here.
      const { data } = await trigger({
        postal_code: values.postal_code.trim().replace(/\s/g, ''),
        housenumber: values.house_number.trim().replace(/\s/g, ''),
      });

      // If there is no data available, return null.
      // The error will be caught and handled by the AddressSelectionFields component.
      if (!data) return null;

      let selection = null;

      // If a selection was previously made, use that to filter the new data.
      if (data.length > 1 && values.selection) {
        selection = data.filter((suggestion) => suggestion[queryMapper.uid] === values.selection);
      }

      // Multiple suggestions available. User should pick one.
      if (data.length > 1 && !selection) {
        helpers?.setFieldError('selection', t('form.error.required'));
        return null;
      }

      // No suggestions available. This is not an HVC address.
      if (data.length === 0 && !selection) {
        return null;
      }

      // Save the selection in the redux state.
      const bagId = selection?.[0]?.[queryMapper.uid] || data[0][queryMapper.uid];
      const selectedAddress = data.find((suggestion) => suggestion[queryMapper.uid] === bagId);

      if (!selectedAddress) {
        return null;
      }

      return selectedAddress;
    },
  };
};

export default useAddressFields;
